/**
 * AthleteProfile.js
 * Athlete self-service profile page: personal info and parent data editing
 */

import React, { useState, useEffect, useCallback } from 'react';
import { Card, Form, Button, Row, Col, Alert, Spinner } from 'react-bootstrap';
import { portalService, medicalCertificateService } from '../../services/api';
import { uploadService } from '../../services/uploadService';
import { CERTIFICATE_TYPES, USER_ROLES } from '../../utils/constants';
import { isMember as checkIsMemberRole } from '../../utils/userHelpers';
import AthleteMedicalCertSection from './AthleteMedicalCertSection';
import AthleteConsentsSection from './AthleteConsentsSection';
import AthleteCustomSectionsList from './AthleteCustomSectionsList';
import { Link } from 'react-router-dom';
import AthleteAttachmentsList from './AthleteAttachmentsList';
import EmailWarningModal from '../Common/EmailWarningModal';
import { checkEmailsForWarnings } from '../../utils/emailValidation';
import '../../styles/AcrosportTheme.css';

const checkHasMemberRole = (user) => {
  if (!user) return false;
  if (user.isMember === true) return true;
  if (checkIsMemberRole(user.roles || user.role)) return true;
  const roles = Array.isArray(user.roles)
    ? user.roles
    : (typeof user.role === 'string' ? [user.role] : []);
  return roles.some(r => typeof r === 'string' && r.toLowerCase() === USER_ROLES.MEMBER);
};

const AthleteProfile = () => {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [userId, setUserId] = useState(null);
  const [isMember, setIsMember] = useState(false);

  // Custom informational sections state
  const [customSections, setCustomSections] = useState([]);
  const [readCustomSections, setReadCustomSections] = useState({});

  // Dynamic portal consents state
  const [consents, setConsents] = useState([]);
  const [readConsents, setReadConsents] = useState({});

  // Portal attachments state
  const [attachments, setAttachments] = useState([]);
  const [readAttachments, setReadAttachments] = useState({});
  const [downloadedAttachments, setDownloadedAttachments] = useState({});
  const [downloadingAttachmentId, setDownloadingAttachmentId] = useState(null);

  // Medical certificate state
  const [currentCertificate, setCurrentCertificate] = useState(null);
  const [isRenewingCertificate, setIsRenewingCertificate] = useState(false);
  const [certForm, setCertForm] = useState({
    certificateType: '',
    issueDate: '',
    expiryDate: ''
  });
  const [certFile, setCertFile] = useState(null);

  const [profileData, setProfileData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    dateOfBirth: '',
    phoneNumber: '',
    address: '',
    fiscalCode: '',
    birthMunicipality: '',
    residenceMunicipality: '',
    residenceProvince: '',
    residencePostalCode: '',
    loginEmail: '',
    createdAt: ''
  });

  // Login email editing state
  const [isEditingLoginEmail, setIsEditingLoginEmail] = useState(false);
  const [newLoginEmail, setNewLoginEmail] = useState('');
  const [loginEmailSaving, setLoginEmailSaving] = useState(false);
  const [loginEmailError, setLoginEmailError] = useState('');
  const [loginEmailSuccess, setLoginEmailSuccess] = useState('');
  const [emailWarningState, setEmailWarningState] = useState({ warnings: [], onIgnore: null });

  const [parentData, setParentData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    phoneNumber: '',
    fiscalCode: '',
    dateOfBirth: '',
    birthMunicipality: '',
    residenceMunicipality: '',
    residenceProvince: '',
    residencePostalCode: '',
    residenceStreet: ''
  });

  const [hasParent, setHasParent] = useState(false);

  const isMinor = () => {
    if (!profileData.dateOfBirth) return false;
    const birthDate = new Date(profileData.dateOfBirth);
    const today = new Date();
    let age = today.getFullYear() - birthDate.getFullYear();
    const m = today.getMonth() - birthDate.getMonth();
    if (m < 0 || (m === 0 && today.getDate() < birthDate.getDate())) {
      age--;
    }
    return age < 18;
  };

  const loadData = useCallback(async () => {
    try {
      setLoading(true);
      setError('');

      const [profileRes, parentRes, sectionsRes, attachmentsRes, consentsRes] = await Promise.all([
        portalService.getProfile(),
        portalService.getParent().catch(() => null),
        portalService.getSections().catch(() => ({ success: false, data: [] })),
        portalService.getAttachments().catch(() => ({ success: false, data: [] })),
        portalService.getConsents().catch(() => ({ success: false, data: [] }))
      ]);

      if (sectionsRes && sectionsRes.success && Array.isArray(sectionsRes.data)) {
        setCustomSections(sectionsRes.data);
        const initialSectionState = {};
        sectionsRes.data.forEach(s => {
          if (s.isAccepted) {
            initialSectionState[s.id] = true;
          }
        });
        setReadCustomSections(initialSectionState);
      } else {
        setCustomSections([]);
      }

      if (attachmentsRes && attachmentsRes.success && Array.isArray(attachmentsRes.data)) {
        setAttachments(attachmentsRes.data);
        const initialAttachmentState = {};
        attachmentsRes.data.forEach(a => {
          if (a.isAccepted) {
            initialAttachmentState[a.id] = true;
          }
        });
        setReadAttachments(initialAttachmentState);
      } else {
        setAttachments([]);
      }

      if (consentsRes && consentsRes.success && Array.isArray(consentsRes.data)) {
        setConsents(consentsRes.data);
        const initialConsentState = {};
        consentsRes.data.forEach(c => {
          if (c.isAccepted) {
            initialConsentState[c.id] = true;
          }
        });
        setReadConsents(initialConsentState);
      } else {
        setConsents([]);
      }

      if (profileRes.success && profileRes.data) {
        const u = profileRes.data;
        setUserId(u.id);
        setIsMember(checkHasMemberRole(u));
        setProfileData({
          firstName: u.firstName || '',
          lastName: u.lastName || '',
          email: u.email || '',
          dateOfBirth: u.dateOfBirth ? u.dateOfBirth.split('T')[0] : '',
          phoneNumber: u.phoneNumber || '',
          address: u.address || '',
          fiscalCode: u.fiscalCode || '',
          birthMunicipality: u.birthMunicipality || '',
          residenceMunicipality: u.residenceMunicipality || '',
          residenceProvince: u.residenceProvince || '',
          residencePostalCode: u.residencePostalCode || '',
          loginEmail: u.loginEmail || '',
          createdAt: u.createdAt || ''
        });

        const p = (parentRes && parentRes.success && parentRes.data) ? parentRes.data : u.parent;
        if (p && (p.firstName || p.lastName || p.email || p.phoneNumber || p.fiscalCode || p.dateOfBirth)) {
          setHasParent(true);
          setParentData({
            firstName: p.firstName || '',
            lastName: p.lastName || '',
            email: p.email || '',
            phoneNumber: p.phoneNumber || '',
            fiscalCode: p.fiscalCode || '',
            dateOfBirth: p.dateOfBirth ? p.dateOfBirth.split('T')[0] : '',
            birthMunicipality: p.birthMunicipality || '',
            residenceMunicipality: p.residenceMunicipality || '',
            residenceProvince: p.residenceProvince || '',
            residencePostalCode: p.residencePostalCode || '',
            residenceStreet: p.residenceStreet || ''
          });
        } else {
          setHasParent(false);
        }

        // Fetch latest medical certificate
        if (u.id) {
          try {
            const certRes = await medicalCertificateService.getUserCertificates(u.id, 'all', 1, 1);
            if (certRes.success && certRes.data && certRes.data.length > 0) {
              setCurrentCertificate(certRes.data[0]);
            } else {
              setCurrentCertificate(null);
            }
          } catch (certErr) {
            console.error('Error loading medical certificate:', certErr);
          }
        }
      } else {
        setError(profileRes?.message || 'Errore nel caricamento del profilo');
      }
    } catch (err) {
      console.error('Error loading athlete profile:', err);
      setError('Errore durante il caricamento dei dati');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const handleProfileChange = (e) => {
    const { name, value } = e.target;
    let processedValue = value;
    if (name === 'residenceProvince') {
      processedValue = value.toUpperCase().slice(0, 2);
    } else if (name === 'residencePostalCode') {
      processedValue = value.replace(/\D/g, '').slice(0, 5);
    } else if (name === 'fiscalCode') {
      processedValue = value.toUpperCase().slice(0, 16);
    }
    setProfileData(prev => ({ ...prev, [name]: processedValue }));
  };

  const handleParentChange = (e) => {
    const { name, value } = e.target;
    let processedValue = value;
    if (name === 'residenceProvince') {
      processedValue = value.toUpperCase().slice(0, 2);
    } else if (name === 'residencePostalCode') {
      processedValue = value.replace(/\D/g, '').slice(0, 5);
    } else if (name === 'fiscalCode') {
      processedValue = value.toUpperCase().slice(0, 16);
    }
    setParentData(prev => ({ ...prev, [name]: processedValue }));
  };

  const handleToggleCustomSectionRead = (sectionId, checked) => {
    setReadCustomSections(prev => ({
      ...prev,
      [sectionId]: checked
    }));
  };

  const handleToggleConsent = (consentId, checked) => {
    setReadConsents(prev => ({
      ...prev,
      [consentId]: checked
    }));
  };

  const handleToggleAttachmentRead = (attachmentId, checked) => {
    setReadAttachments(prev => ({
      ...prev,
      [attachmentId]: checked
    }));
  };

  const handleDownloadAttachmentFile = async (attachment) => {
    setDownloadingAttachmentId(attachment.id);
    try {
      const res = await portalService.downloadAttachment(attachment);
      if (!res.success) {
        setError(res.message || 'Errore nel download del documento');
      } else {
        setDownloadedAttachments(prev => ({
          ...prev,
          [attachment.id]: true
        }));
      }
    } catch (err) {
      console.error('Error downloading attachment:', err);
      setError('Errore durante il download del documento');
    } finally {
      setDownloadingAttachmentId(null);
    }
  };

  const handleUpdateLoginEmail = async (e, bypassEmailWarning = false) => {
    if (e && e.preventDefault) e.preventDefault();
    const trimmed = newLoginEmail.trim();
    if (!trimmed) {
      setLoginEmailError('L\'email di login è obbligatoria');
      return;
    }

    if (!bypassEmailWarning) {
      const warnings = checkEmailsForWarnings([
        {
          label: 'Nuova Email di Login',
          value: trimmed,
          selector: 'input[data-testid="new-portal-login-email-input"]',
        },
      ]);
      if (warnings.length > 0) {
        setEmailWarningState({
          warnings,
          onIgnore: () => handleUpdateLoginEmail(null, true),
        });
        return;
      }
    }

    try {
      setLoginEmailSaving(true);
      setLoginEmailError('');
      setLoginEmailSuccess('');

      const res = await portalService.updateLoginEmail(trimmed, userId);
      if (res.success) {
        setProfileData(prev => ({ ...prev, loginEmail: trimmed }));
        setLoginEmailSuccess(res.message || 'Email di login aggiornata con successo');
        setIsEditingLoginEmail(false);
      } else {
        setLoginEmailError(res.message || 'Errore durante l\'aggiornamento dell\'email di login');
      }
    } catch (err) {
      console.error('Error updating athlete login email:', err);
      setLoginEmailError('Errore di connessione al server');
    } finally {
      setLoginEmailSaving(false);
    }
  };

  const unacceptedMandatoryConsents = consents.filter(
    c => c.isEnabled !== false && c.isMandatory && !readConsents[c.id]
  );
  const unreadSections = customSections.filter(
    s => s.enabled !== false && !s.isAccepted && !readCustomSections[s.id]
  );
  const unacceptedAttachments = attachments.filter(
    a => a.isEnabled !== false && !a.isAccepted && !readAttachments[a.id]
  );

  const allCustomSectionsRead = unreadSections.length === 0;
  const allAttachmentsAccepted = unacceptedAttachments.length === 0;
  const allMandatoryConsentsAccepted = unacceptedMandatoryConsents.length === 0;
  const isSaveDisabled = saving || !allCustomSectionsRead || !allAttachmentsAccepted || !allMandatoryConsentsAccepted;

  const handleSubmit = async (e, bypassEmailWarning = false) => {
    if (e && e.preventDefault) e.preventDefault();

    // Validazione consensi obbligatori
    if (unacceptedMandatoryConsents.length > 0) {
      setError(`È necessario esprimere tutti i consensi obbligatori prima di procedere (${unacceptedMandatoryConsents.map(c => `"${c.title}"`).join(', ')}).`);
      const targetEl = document.getElementById('portal-athlete-consents-section');
      if (targetEl) {
        targetEl.scrollIntoView({ behavior: 'smooth' });
      } else {
        window.scrollTo({ top: document.body.scrollHeight, behavior: 'smooth' });
      }
      return;
    }

    // Validazione presa visione sezioni informative
    if (unreadSections.length > 0) {
      setError(`È necessario confermare la presa visione di tutte le sezioni informative prima di procedere (${unreadSections.map(s => `"${s.title}"`).join(', ')}).`);
      const targetEl = document.getElementById('portal-athlete-custom-sections');
      if (targetEl) {
        targetEl.scrollIntoView({ behavior: 'smooth' });
      } else {
        window.scrollTo({ top: document.body.scrollHeight, behavior: 'smooth' });
      }
      return;
    }

    // Validazione lettura e accettazione allegati
    if (unacceptedAttachments.length > 0) {
      setError(`È necessario confermare la lettura e accettazione di tutti gli allegati prima di procedere (${unacceptedAttachments.map(a => `"${a.title}"`).join(', ')}).`);
      const targetEl = document.getElementById('portal-athlete-attachments-section');
      if (targetEl) {
        targetEl.scrollIntoView({ behavior: 'smooth' });
      } else {
        window.scrollTo({ top: document.body.scrollHeight, behavior: 'smooth' });
      }
      return;
    }

    // Validazione dati anagrafici atleta obbligatori
    if (!profileData.firstName.trim() || !profileData.lastName.trim()) {

      setError('Nome e cognome sono obbligatori');
      window.scrollTo({ top: 0, behavior: 'smooth' });
      return;
    }

    if (!profileData.dateOfBirth) {

      setError('La data di nascita è obbligatoria');
      window.scrollTo({ top: 0, behavior: 'smooth' });
      return;
    }

    if (!profileData.birthMunicipality?.trim()) {

      setError('Il luogo di nascita è obbligatorio');
      window.scrollTo({ top: 0, behavior: 'smooth' });
      return;
    }

    if (!profileData.fiscalCode.trim()) {

      setError('Il codice fiscale è obbligatorio');
      window.scrollTo({ top: 0, behavior: 'smooth' });
      return;
    }

    if (!profileData.address?.trim()) {

      setError('L\'indirizzo di residenza è obbligatorio');
      window.scrollTo({ top: 0, behavior: 'smooth' });
      return;
    }

    if (!profileData.residenceMunicipality?.trim()) {

      setError('Il comune di residenza è obbligatorio');
      window.scrollTo({ top: 0, behavior: 'smooth' });
      return;
    }

    if (!profileData.residenceProvince?.trim()) {

      setError('La provincia di residenza è obbligatoria');
      window.scrollTo({ top: 0, behavior: 'smooth' });
      return;
    }

    if (!profileData.residencePostalCode?.trim()) {

      setError('Il CAP di residenza è obbligatorio');
      window.scrollTo({ top: 0, behavior: 'smooth' });
      return;
    }

    // Validazione dati di contatto obbligatori (solo per maggiorenni)
    if (!isMinor()) {
      if (!profileData.email.trim()) {
        setError('L\'email di contatto è obbligatoria');
        window.scrollTo({ top: 0, behavior: 'smooth' });
        return;
      }

      if (!profileData.phoneNumber?.trim()) {
        setError('Il recapito telefonico è obbligatorio');
        window.scrollTo({ top: 0, behavior: 'smooth' });
        return;
      }
    }

    // Validazione dati genitore per atleti minorenni
    if (isMinor()) {
      if (!parentData.firstName?.trim() || !parentData.lastName?.trim()) {

        setError('Nome e cognome del genitore/tutore sono obbligatori per gli atleti minorenni');
        window.scrollTo({ top: 0, behavior: 'smooth' });
        return;
      }
      if (!parentData.email?.trim()) {

        setError('L\'email del genitore/tutore è obbligatoria per gli atleti minorenni');
        window.scrollTo({ top: 0, behavior: 'smooth' });
        return;
      }
      if (!parentData.phoneNumber?.trim()) {

        setError('Il telefono del genitore/tutore è obbligatorio per gli atleti minorenni');
        window.scrollTo({ top: 0, behavior: 'smooth' });
        return;
      }
      if (!parentData.fiscalCode?.trim()) {

        setError('Il codice fiscale del genitore/tutore è obbligatorio per gli atleti minorenni');
        window.scrollTo({ top: 0, behavior: 'smooth' });
        return;
      }
      if (!parentData.dateOfBirth) {

        setError('La data di nascita del genitore/tutore è obbligatoria per gli atleti minorenni');
        window.scrollTo({ top: 0, behavior: 'smooth' });
        return;
      }
      if (!parentData.birthMunicipality?.trim()) {

        setError('Il luogo di nascita del genitore/tutore è obbligatorio per gli atleti minorenni');
        window.scrollTo({ top: 0, behavior: 'smooth' });
        return;
      }
      if (!parentData.residenceStreet?.trim()) {

        setError('L\'indirizzo di residenza del genitore/tutore è obbligatorio per gli atleti minorenni');
        window.scrollTo({ top: 0, behavior: 'smooth' });
        return;
      }
      if (!parentData.residenceMunicipality?.trim()) {

        setError('Il comune di residenza del genitore/tutore è obbligatorio per gli atleti minorenni');
        window.scrollTo({ top: 0, behavior: 'smooth' });
        return;
      }
      if (!parentData.residenceProvince?.trim()) {

        setError('La provincia di residenza del genitore/tutore è obbligatoria per gli atleti minorenni');
        window.scrollTo({ top: 0, behavior: 'smooth' });
        return;
      }
      if (!parentData.residencePostalCode?.trim()) {

        setError('Il CAP di residenza del genitore/tutore è obbligatorio per gli atleti minorenni');
        window.scrollTo({ top: 0, behavior: 'smooth' });
        return;
      }
    }

    // Validazione facoltativa certificato medico (se compilato)
    const hasCertData = Boolean(certForm.issueDate || certForm.expiryDate || certFile);
    if (hasCertData) {
      if (!certForm.issueDate || !certForm.expiryDate) {
        setError('Per registrare il certificato medico, indica sia la data di rilascio che la data di scadenza.');
        window.scrollTo({ top: 0, behavior: 'smooth' });
        return;
      }

      if (!certForm.certificateType) {
        setError('Per registrare il certificato medico, seleziona il tipo di certificato.');
        window.scrollTo({ top: 0, behavior: 'smooth' });
        return;
      }

      if (new Date(certForm.expiryDate) < new Date(certForm.issueDate)) {
        setError('La data di scadenza del certificato medico non può essere precedente alla data di rilascio.');
        window.scrollTo({ top: 0, behavior: 'smooth' });
        return;
      }

      if (new Date(certForm.expiryDate) <= new Date()) {
        setError('La data di scadenza del certificato medico deve essere una data futura.');
        window.scrollTo({ top: 0, behavior: 'smooth' });
        return;
      }
    }

    if (!bypassEmailWarning) {
      const fieldsToCheck = [
        {
          label: isMinor() ? 'Email di Contatto Atleta' : 'Email di Contatto',
          value: profileData.email,
          fieldId: 'portal-athlete-email',
          selector: 'input[name="email"]',
        },
        {
          label: 'Email Genitore',
          value: parentData?.email,
          fieldId: 'portal-parent-email',
        },
      ];
      const warnings = checkEmailsForWarnings(fieldsToCheck);
      if (warnings.length > 0) {
        setEmailWarningState({
          warnings,
          onIgnore: () => handleSubmit(null, true),
        });
        return;
      }
    }

    try {
      setSaving(true);
      setError('');
      setSuccess('');

      // Save medical certificate if entered / renewed before updating profile
      if (certForm.issueDate && certForm.expiryDate) {
        const certPayload = {
          userId: userId,
          certificateType: certForm.certificateType || CERTIFICATE_TYPES.NON_COMPETITIVE,
          issueDate: certForm.issueDate,
          expiryDate: certForm.expiryDate,
          file: certFile
        };

        const uploadCertRes = await uploadService.uploadCertificate(certPayload);
        if (!uploadCertRes.success) {
          setError(uploadCertRes.message || 'Errore durante il caricamento del certificato medico');
          return;
        }
      }

      // If athlete is minor or has parent data filled in, update parent
      let parentPayload = null;
      if (isMinor() || hasParent || parentData.firstName || parentData.lastName || parentData.email) {
        parentPayload = {
          ...parentData,
          dateOfBirth: parentData.dateOfBirth ? new Date(parentData.dateOfBirth).toISOString() : null
        };
        const updateParentRes = await portalService.updateParent(parentPayload);
        if (!updateParentRes.success) {
          setError(updateParentRes.message || 'Errore durante il salvataggio dei dati del genitore');
          return;
        }
      }

      const profilePayload = {
        firstName: profileData.firstName.trim(),
        lastName: profileData.lastName.trim(),
        email: isMinor()
          ? (profileData.email?.trim() || parentData.email?.trim() || '')
          : profileData.email.trim(),
        fiscalCode: profileData.fiscalCode.trim(),
        dateOfBirth: profileData.dateOfBirth ? `${profileData.dateOfBirth}T00:00:00.000Z` : null,
        phoneNumber: isMinor() ? (profileData.phoneNumber?.trim() || null) : (profileData.phoneNumber?.trim() || null),
        address: profileData.address?.trim() || null,
        birthMunicipality: profileData.birthMunicipality?.trim() || null,
        residenceMunicipality: profileData.residenceMunicipality?.trim() || null,
        residenceProvince: profileData.residenceProvince?.trim() || null,
        residencePostalCode: profileData.residencePostalCode?.trim() || null,
        isMember: (() => {
          const membershipConsent = consents.find(c => c.code === 'membership_request' || (c.title && c.title.toLowerCase().includes('socio')));
          return membershipConsent ? Boolean(readConsents[membershipConsent.id]) : isMember;
        })(),
        acceptedConsentIds: consents
          .filter(c => Boolean(readConsents[c.id]))
          .map(c => c.id),
        rejectedConsentIds: consents
          .filter(c => !c.isMandatory && !readConsents[c.id])
          .map(c => c.id),
        acceptedAttachmentIds: attachments
          .filter(a => a.isAccepted || Boolean(readAttachments[a.id]))
          .map(a => a.id),
        acceptedSectionIds: customSections
          .filter(s => s.isAccepted || Boolean(readCustomSections[s.id]))
          .map(s => s.id),
        parent: parentPayload
      };

      const updateProfileRes = await portalService.updateProfile(profilePayload);

      if (!updateProfileRes.success) {
        setError(updateProfileRes.message || 'Errore durante l\'aggiornamento del profilo');
        return;
      }

      setIsRenewingCertificate(false);
      setCertFile(null);
      setCertForm({
        certificateType: '',
        issueDate: '',
        expiryDate: ''
      });

      await loadData();

      const recipientEmail = updateProfileRes.data?.recipientEmail
        || (isMinor() ? (parentData.email?.trim() || profileData.email?.trim()) : profileData.email?.trim());

      const notice = 'Si ricorda di firmare e consegnare i documenti in segreteria.';
      const baseMsg = updateProfileRes.data?.message
        || updateProfileRes.message
        || `I tuoi dati sono stati aggiornati con successo! Il modulo di iscrizione aggiornato e tutti gli allegati sono stati inviati via email a ${recipientEmail}.`;

      const successMsg = baseMsg.includes('firmare e consegnare')
        ? baseMsg
        : `${baseMsg} ${notice}`;

      setSuccess(successMsg);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    } catch (err) {
      console.error('Error saving profile:', err);
      setError('Errore durante il salvataggio dei dati');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="text-center py-5">
        <Spinner animation="border" variant="primary" />
        <p className="mt-3 text-muted">Caricamento anagrafica...</p>
      </div>
    );
  }

  return (
    <div className="athlete-profile acrosport-theme">
      <div className="d-flex flex-column flex-md-row justify-content-between align-items-md-center gap-3 mb-4">
        <div>
          <h2 className="mb-1 fw-bold">
            I Miei Dati Anagrafici
          </h2>
          <p className="text-muted mb-0">
            Visualizza e mantieni aggiornate le tue informazioni personali per A.S.D. Acrosport Cesena.
          </p>
        </div>
        <div>
          <Link
            to="/portal/bookings"
            className="btn btn-outline-primary rounded-pill d-inline-flex align-items-center gap-2 px-3 py-2 shadow-sm"
          >
            <i className="fas fa-calendar-check text-primary"></i>
            <span>Lezioni & Prenotazioni</span>
            <i className="fas fa-arrow-right small"></i>
          </Link>
        </div>
      </div>

      {/* Banner rapido di accesso a Lezioni & Prenotazioni */}
      <Card className="mb-4 border-0 shadow-sm bg-primary-subtle text-primary-emphasis border-start border-4 border-primary">
        <Card.Body className="d-flex flex-column flex-sm-row justify-content-between align-items-sm-center gap-3 py-3">
          <div className="d-flex align-items-center gap-3">
            <div
              className="rounded-circle bg-primary text-white d-flex align-items-center justify-content-center"
              style={{ width: 44, height: 44, minWidth: 44 }}
            >
              <i className="fas fa-calendar-alt fs-5"></i>
            </div>
            <div>
              <h6 className="mb-1 fw-bold text-dark">Prenotazioni & Calendario Lezioni</h6>
              <p className="mb-0 text-muted small">
                Accedi alla pagina dedicata per prenotare i tuoi slot e consultare le tue lezioni programmate.
              </p>
            </div>
          </div>
          <Link
            to="/portal/bookings"
            className="btn btn-primary rounded-pill px-4 py-2 text-nowrap fw-medium shadow-sm align-self-start align-self-sm-center"
          >
            <i className="fas fa-calendar-plus me-2"></i> Vai alle Prenotazioni
          </Link>
        </Card.Body>
      </Card>

      {error && <Alert variant="danger" onClose={() => setError('')} dismissible>{error}</Alert>}
      {success && <Alert variant="success" onClose={() => setSuccess('')} dismissible>{success}</Alert>}

      <Form onSubmit={handleSubmit}>
        {/* Parent / Guardian section for minors */}
        {(isMinor() || hasParent) && (
          <Card className="mb-4 shadow-sm border-0">
            <Card.Header className="bg-primary text-white py-3 border-bottom">
              <h5 className="mb-0 text-white">
                <i className="fas fa-user-friends me-2"></i>
                Dati Genitore / Tutore Legale {isMinor() && <span className="text-warning small">*(Obbligatorio per minorenni)*</span>}
              </h5>
            </Card.Header>
            <Card.Body>
              <Row>
                <Col md={6}>
                  <Form.Group className="mb-3" controlId="portal-parent-first-name">
                    <Form.Label>Nome Genitore {isMinor() && '*'}</Form.Label>
                    <Form.Control
                      type="text"
                      name="firstName"
                      value={parentData.firstName}
                      onChange={handleParentChange}
                      placeholder="Nome"
                      required={isMinor()}
                    />
                  </Form.Group>
                </Col>
                <Col md={6}>
                  <Form.Group className="mb-3" controlId="portal-parent-last-name">
                    <Form.Label>Cognome Genitore {isMinor() && '*'}</Form.Label>
                    <Form.Control
                      type="text"
                      name="lastName"
                      value={parentData.lastName}
                      onChange={handleParentChange}
                      placeholder="Cognome"
                      required={isMinor()}
                    />
                  </Form.Group>
                </Col>
              </Row>

              <Row>
                <Col md={6}>
                  <Form.Group className="mb-3" controlId="portal-parent-email">
                    <Form.Label>Email Genitore {isMinor() && '*'}</Form.Label>
                    <Form.Control
                      type="email"
                      name="email"
                      value={parentData.email}
                      onChange={handleParentChange}
                      placeholder="email@genitore.com"
                      required={isMinor()}
                    />
                  </Form.Group>
                </Col>
                <Col md={6}>
                  <Form.Group className="mb-3" controlId="portal-parent-phone">
                    <Form.Label>Telefono Genitore {isMinor() && '*'}</Form.Label>
                    <Form.Control
                      type="tel"
                      name="phoneNumber"
                      value={parentData.phoneNumber}
                      onChange={handleParentChange}
                      placeholder="+39 333 1234567"
                      required={isMinor()}
                    />
                  </Form.Group>
                </Col>
              </Row>

              <Row>
                <Col md={6}>
                  <Form.Group className="mb-3" controlId="portal-parent-fiscal-code">
                    <Form.Label>Codice Fiscale Genitore {isMinor() && '*'}</Form.Label>
                    <Form.Control
                      type="text"
                      name="fiscalCode"
                      value={parentData.fiscalCode}
                      onChange={handleParentChange}
                      placeholder="RSSMRA80A01H501X"
                      maxLength={16}
                      required={isMinor()}
                    />
                  </Form.Group>
                </Col>
                <Col md={6}>
                  <Form.Group className="mb-3" controlId="portal-parent-date-of-birth">
                    <Form.Label>Data di Nascita Genitore {isMinor() && '*'}</Form.Label>
                    <Form.Control
                      type="date"
                      name="dateOfBirth"
                      value={parentData.dateOfBirth}
                      onChange={handleParentChange}
                      required={isMinor()}
                    />
                  </Form.Group>
                </Col>
              </Row>

              <Row>
                <Col md={6}>
                  <Form.Group className="mb-3" controlId="portal-parent-birth-municipality">
                    <Form.Label>Luogo di Nascita Genitore {isMinor() && '*'}</Form.Label>
                    <Form.Control
                      type="text"
                      name="birthMunicipality"
                      value={parentData.birthMunicipality}
                      onChange={handleParentChange}
                      placeholder="Milano"
                      required={isMinor()}
                    />
                  </Form.Group>
                </Col>
                <Col md={6}>
                  <Form.Group className="mb-3" controlId="portal-parent-residence-street">
                    <Form.Label>Indirizzo di Residenza Genitore {isMinor() && '*'}</Form.Label>
                    <Form.Control
                      type="text"
                      name="residenceStreet"
                      value={parentData.residenceStreet}
                      onChange={handleParentChange}
                      placeholder="Via Roma 1"
                      required={isMinor()}
                    />
                  </Form.Group>
                </Col>
              </Row>

              <Row>
                <Col md={4}>
                  <Form.Group className="mb-3" controlId="portal-parent-residence-municipality">
                    <Form.Label>Comune {isMinor() && '*'}</Form.Label>
                    <Form.Control
                      type="text"
                      name="residenceMunicipality"
                      value={parentData.residenceMunicipality}
                      onChange={handleParentChange}
                      placeholder="Milano"
                      required={isMinor()}
                    />
                  </Form.Group>
                </Col>
                <Col md={4}>
                  <Form.Group className="mb-3" controlId="portal-parent-residence-province">
                    <Form.Label>Provincia {isMinor() && '*'}</Form.Label>
                    <Form.Control
                      type="text"
                      name="residenceProvince"
                      value={parentData.residenceProvince}
                      onChange={handleParentChange}
                      placeholder="MI"
                      maxLength={2}
                      required={isMinor()}
                    />
                  </Form.Group>
                </Col>
                <Col md={4}>
                  <Form.Group className="mb-3" controlId="portal-parent-residence-postal-code">
                    <Form.Label>CAP {isMinor() && '*'}</Form.Label>
                    <Form.Control
                      type="text"
                      name="residencePostalCode"
                      value={parentData.residencePostalCode}
                      onChange={handleParentChange}
                      placeholder="20100"
                      maxLength={5}
                      required={isMinor()}
                    />
                  </Form.Group>
                </Col>
              </Row>
            </Card.Body>
          </Card>
        )}

        {/* Personal Information */}
        <Card className="mb-4 shadow-sm border-0">
          <Card.Header className="bg-primary text-white py-3 border-bottom">
            <h5 className="mb-0 text-white">
              <i className="fas fa-user me-2"></i>
              Dati Anagrafici Atleta
            </h5>
          </Card.Header>
          <Card.Body>
            <Row>
              <Col md={6}>
                <Form.Group className="mb-3" controlId="portal-athlete-first-name">
                  <Form.Label>Nome *</Form.Label>
                  <Form.Control
                    type="text"
                    name="firstName"
                    value={profileData.firstName}
                    onChange={handleProfileChange}
                    placeholder="Mario"
                    required
                  />
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group className="mb-3" controlId="portal-athlete-last-name">
                  <Form.Label>Cognome *</Form.Label>
                  <Form.Control
                    type="text"
                    name="lastName"
                    value={profileData.lastName}
                    onChange={handleProfileChange}
                    placeholder="Rossi"
                    required
                  />
                </Form.Group>
              </Col>
            </Row>

            <Row>
              <Col md={6}>
                <Form.Group className="mb-3" controlId="portal-athlete-date-of-birth">
                  <Form.Label>Data di Nascita *</Form.Label>
                  <Form.Control
                    type="date"
                    name="dateOfBirth"
                    value={profileData.dateOfBirth}
                    onChange={handleProfileChange}
                    required
                  />
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group className="mb-3" controlId="portal-athlete-birth-municipality">
                  <Form.Label>Luogo di Nascita *</Form.Label>
                  <Form.Control
                    type="text"
                    name="birthMunicipality"
                    value={profileData.birthMunicipality}
                    onChange={handleProfileChange}
                    placeholder="Milano"
                    required
                  />
                </Form.Group>
              </Col>
            </Row>

            <Row>
              <Col md={12}>
                <Form.Group className="mb-3" controlId="portal-athlete-fiscal-code">
                  <Form.Label>Codice Fiscale *</Form.Label>
                  <Form.Control
                    type="text"
                    name="fiscalCode"
                    value={profileData.fiscalCode}
                    onChange={handleProfileChange}
                    placeholder="RSSMRA90A01H501X"
                    maxLength={16}
                    required
                  />
                </Form.Group>
              </Col>
            </Row>

            <Form.Group className="mb-3" controlId="portal-athlete-address">
              <Form.Label>Indirizzo di Residenza *</Form.Label>
              <Form.Control
                type="text"
                name="address"
                value={profileData.address}
                onChange={handleProfileChange}
                placeholder="Via Roma 123"
                required
              />
            </Form.Group>

            <Row>
              <Col md={4}>
                <Form.Group className="mb-3" controlId="portal-athlete-residence-municipality">
                  <Form.Label>Comune *</Form.Label>
                  <Form.Control
                    type="text"
                    name="residenceMunicipality"
                    value={profileData.residenceMunicipality}
                    onChange={handleProfileChange}
                    placeholder="Milano"
                    required
                  />
                </Form.Group>
              </Col>
              <Col md={4}>
                <Form.Group className="mb-3" controlId="portal-athlete-residence-province">
                  <Form.Label>Provincia *</Form.Label>
                  <Form.Control
                    type="text"
                    name="residenceProvince"
                    value={profileData.residenceProvince}
                    onChange={handleProfileChange}
                    placeholder="MI"
                    maxLength={2}
                    required
                  />
                </Form.Group>
              </Col>
              <Col md={4}>
                <Form.Group className="mb-3" controlId="portal-athlete-residence-postal-code">
                  <Form.Label>CAP *</Form.Label>
                  <Form.Control
                    type="text"
                    name="residencePostalCode"
                    value={profileData.residencePostalCode}
                    onChange={handleProfileChange}
                    placeholder="20100"
                    maxLength={5}
                    required
                  />
                </Form.Group>
              </Col>
            </Row>
          </Card.Body>
        </Card>

        {/* Contact Information */}
        {!isMinor() && (
          <Card className="mb-4 shadow-sm border-0">
            <Card.Header className="bg-primary text-white py-3 border-bottom">
              <h5 className="mb-0 text-white">
                <i className="fas fa-envelope me-2"></i>
                Informazioni di Contatto
              </h5>
            </Card.Header>
            <Card.Body>
              <Row>
                <Col md={6}>
                  <Form.Group className="mb-3" controlId="portal-athlete-email">
                    <Form.Label>Email di Contatto *</Form.Label>
                    <Form.Control
                      type="email"
                      name="email"
                      value={profileData.email}
                      onChange={handleProfileChange}
                      placeholder="email@esempio.com"
                      required
                    />
                    <Form.Text className="text-muted">
                      Email usata per comunicazioni e notifiche dell'associazione.
                    </Form.Text>
                  </Form.Group>
                </Col>
                <Col md={6}>
                  <Form.Group className="mb-3" controlId="portal-athlete-phone">
                    <Form.Label>Telefono *</Form.Label>
                    <Form.Control
                      type="tel"
                      name="phoneNumber"
                      value={profileData.phoneNumber}
                      onChange={handleProfileChange}
                      placeholder="+39 333 1234567"
                      required
                    />
                  </Form.Group>
                </Col>
              </Row>
            </Card.Body>
          </Card>
        )}

        {/* Medical Certificate Card */}
        <AthleteMedicalCertSection
          isRegistration={false}
          currentCertificate={currentCertificate}
          isRenewingCertificate={isRenewingCertificate}
          setIsRenewingCertificate={setIsRenewingCertificate}
          certForm={certForm}
          setCertForm={setCertForm}
          certFile={certFile}
          setCertFile={setCertFile}
          onFileValidationError={(err) => setError(err)}
          onFileDeleted={() => loadData()}
        />

        {/* Consents & Privacy Card */}
        <AthleteConsentsSection
          consents={consents.map(c => ({
            ...c,
            isAccepted: Boolean(readConsents[c.id])
          }))}
          onToggleConsent={handleToggleConsent}
          isRegistration={false}
          readOnlyLocked={true}
        />

        {/* Sezioni Informative Personalizzate (Pagamenti, Safeguarding, ecc.) */}
        <AthleteCustomSectionsList
          customSections={customSections}
          readCustomSections={readCustomSections}
          onToggleRead={handleToggleCustomSectionRead}
          isRegistration={false}
        />

        {/* Account Info & Email di Login Portale */}
        <Card className="mb-4 shadow-sm border-0 bg-light">
          <Card.Body>
            <Row className="align-items-center">
              <Col md={6}>
                <div className="d-flex justify-content-between align-items-center mb-1">
                  <small className="text-muted fw-bold">Email di Login Portale</small>
                  {!isEditingLoginEmail && (
                    <Button
                      variant="outline-primary"
                      size="sm"
                      onClick={() => {
                        setIsEditingLoginEmail(true);
                        setNewLoginEmail(profileData.loginEmail || '');
                        setLoginEmailError('');
                        setLoginEmailSuccess('');
                      }}
                      data-testid="edit-portal-login-email-btn"
                    >
                      <i className="fas fa-pencil-alt me-1"></i> Modifica
                    </Button>
                  )}
                </div>

                {loginEmailSuccess && (
                  <Alert variant="success" className="py-1 px-2 mb-2 small" dismissible onClose={() => setLoginEmailSuccess('')}>
                    {loginEmailSuccess}
                  </Alert>
                )}

                {!isEditingLoginEmail ? (
                  <strong className="d-block text-primary font-monospace" data-testid="current-portal-login-email">
                    {profileData.loginEmail || <span className="text-muted fst-italic">Non configurata</span>}
                  </strong>
                ) : (
                  <div className="mt-2">
                    <Form.Group className="mb-2">
                      <Form.Control
                        type="email"
                        size="sm"
                        value={newLoginEmail}
                        onChange={(e) => {
                          setNewLoginEmail(e.target.value);
                          if (loginEmailError) setLoginEmailError('');
                        }}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter') {
                            e.preventDefault();
                            handleUpdateLoginEmail(e);
                          }
                        }}
                        isInvalid={!!loginEmailError}
                        placeholder="Inserisci la nuova email di login"
                        data-testid="new-portal-login-email-input"
                        disabled={loginEmailSaving}
                        required
                      />
                      <Form.Control.Feedback type="invalid" className="small">
                        {loginEmailError}
                      </Form.Control.Feedback>
                    </Form.Group>
                    <div className="d-flex gap-2">
                      <Button
                        type="button"
                        variant="primary"
                        size="sm"
                        disabled={loginEmailSaving}
                        onClick={handleUpdateLoginEmail}
                        data-testid="save-portal-login-email-btn"
                      >
                        {loginEmailSaving ? (
                          <>
                            <Spinner animation="border" size="sm" className="me-1" /> Salvataggio...
                          </>
                        ) : (
                          <>
                            <i className="fas fa-save me-1"></i> Salva
                          </>
                        )}
                      </Button>
                      <Button
                        type="button"
                        variant="outline-secondary"
                        size="sm"
                        disabled={loginEmailSaving}
                        onClick={() => {
                          setIsEditingLoginEmail(false);
                          setLoginEmailError('');
                        }}
                      >
                        Annulla
                      </Button>
                    </div>
                  </div>
                )}
              </Col>
              {profileData.createdAt && (
                <Col md={6} className="mt-3 mt-md-0">
                  <small className="text-muted d-block">Data Iscrizione</small>
                  <span>{new Date(profileData.createdAt).toLocaleDateString('it-IT')}</span>
                </Col>
              )}
            </Row>
          </Card.Body>
        </Card>



        {/* Allegati Portale Atleta */}
        <AthleteAttachmentsList
          attachments={attachments}
          readAttachments={readAttachments}
          downloadedAttachments={downloadedAttachments}
          downloadingAttachmentId={downloadingAttachmentId}
          onDownloadAttachment={handleDownloadAttachmentFile}
          onToggleRead={handleToggleAttachmentRead}
          isRegistration={false}
        />

        {/* Actions */}
        <div className="d-flex flex-column align-items-end mb-5">
          {(!allCustomSectionsRead || !allAttachmentsAccepted || !allMandatoryConsentsAccepted) && (
            <div className="alert alert-warning py-2 px-3 mb-3 small text-start w-100 shadow-sm border" data-testid="portal-save-requirements-warning">
              <div className="fw-semibold mb-1 text-danger">
                <i className="fas fa-exclamation-triangle me-1"></i>
                Per salvare le modifiche è necessario completare tutti i consensi obbligatori, confermare la lettura di tutte le sezioni informative e di tutti gli allegati.
              </div>
              <ul className="mb-0 ps-3 text-secondary">
                {unacceptedMandatoryConsents.length > 0 && (
                  <li>
                    <span className="fw-semibold text-danger">Consensi obbligatori da sottoscrivere:</span>{' '}
                    {unacceptedMandatoryConsents.map(c => `"${c.title}"`).join(', ')}
                  </li>
                )}
                {unreadSections.length > 0 && (
                  <li>
                    <span className="fw-semibold text-danger">Sezioni informative da confermare:</span>{' '}
                    {unreadSections.map(s => `"${s.title}"`).join(', ')}
                  </li>
                )}
                {unacceptedAttachments.length > 0 && (
                  <li>
                    <span className="fw-semibold text-danger">Allegati da scaricare e accettare:</span>{' '}
                    {unacceptedAttachments.map(a => `"${a.title}"`).join(', ')}
                  </li>
                )}
              </ul>
            </div>
          )}
          <Button
            type="submit"
            variant="primary"
            size="lg"
            disabled={isSaveDisabled}
            className="btn-acro-primary px-5 shadow-sm"
          >
            {saving ? (
              <>
                <Spinner animation="border" size="sm" className="me-2" />
                Salvataggio in corso...
              </>
            ) : (
              <>
                <i className="fas fa-save me-2"></i>
                Salva Modifiche
              </>
            )}
          </Button>
        </div>
      </Form>

      {/* Email Warning Modal */}
      <EmailWarningModal
        show={emailWarningState.warnings.length > 0}
        warnings={emailWarningState.warnings}
        onCorrect={() => setEmailWarningState({ warnings: [], onIgnore: null })}
        onIgnoreAndProceed={() => {
          const proceed = emailWarningState.onIgnore;
          setEmailWarningState({ warnings: [], onIgnore: null });
          if (proceed) proceed();
        }}
      />
    </div>
  );
};

export default AthleteProfile;
